import { useState } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { LinkOff } from '@mui/icons-material';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import {
  BankAccountOperationSourceLabels,
  ActionRefLabels,
} from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import getActionLink from 'helpers/lp/getActionLink';

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'none',
}));

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
  };
};

type Row = {
  id: string;
};

const ReconciledTransactions: React.FC<Props> = ({ bankAccount }) => {
  const pageId = `reconciled-transactions-${bankAccount.id}`;
  const [rowToUnreconcile, setRowToUnreconcile] = useState<Row | null>(null);
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const pagination = usePagination({
    id: pageId,
    orderBy: 'date',
    order: 'desc',
  });

  const [data, listBankAccountOperationsQuery] =
    api.bankAccount.listBankAccountOperations.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
      status: BankAccountOperationStatus.Reconciled,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      orderBy: 'date',
      order: 'desc',
    });

  const operationIds = data.results.map((r) => r.id);

  const [actionsByOperationIdArray, listBankAccountOperationActionsQuery] =
    api.bankAccount.listBankAccountOperationActions.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
      operationIds,
    });

  const unreconcile = api.bankAccount.unreconcileOperation.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Transaction unreconciled successfully.',
      refetchType: 'none',
      onSuccess: () => {
        setRowToUnreconcile(null);
        listBankAccountOperationsQuery.refetch();
        listBankAccountOperationActionsQuery.refetch();
      },
    }),
  );

  const actionsByOperationId = new Map(
    actionsByOperationIdArray.map(({ operationId, actions }) => [
      operationId,
      actions,
    ]),
  );

  type OperationRow = (typeof data)['results'][number];
  type ActionRow =
    (typeof actionsByOperationIdArray)[number]['actions'][number];

  const actionColumns: Array<TableColumn<ActionRow>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      weight: 1,
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'actionRef',
      label: 'Action',
      weight: 1,
      render: (row) => {
        const link = getActionLink(
          row.actionRef,
          row.id,
          row.incomeOrExpenseGroupId,
        );
        const label = ActionRefLabels[row.actionRef];
        return link ? <StyledLink to={link}>{label}</StyledLink> : label;
      },
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      weight: 1,
    },
    {
      key: 'description',
      label: 'Description',
      weight: 1,
    },
    {
      key: 'unreconciledAmountTcy',
      label: 'Unreconciled amount',
      weight: 1,
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
    },
    {
      key: 'reconciledAmountTcy',
      label: 'Amount reconciled',
      weight: 1,
      render: (row) =>
        numberToCurrencyString(row.reconciledAmountTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
    },
    {
      key: 'actionsspacer',
      label: '',
      width: 104,
    },
  ];

  const columns: Array<TableColumn<OperationRow>> = [
    {
      key: 'date',
      label: 'Date',
      weight: 1,
      render: (row) => row.date.toLabel(),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      weight: 1,
    },
    {
      key: 'description',
      label: 'Description',
      weight: 1,
    },
    {
      key: 'spent',
      label: 'Spent',
      weight: 1,
      render: (row) =>
        numberToCurrencyString(
          row.spent !== undefined ? Math.abs(row.spent) : undefined,
          { fallback: '-', currency: row.accountCurrency },
        ),
    },
    {
      key: 'received',
      label: 'Received',
      weight: 1,
      render: (row) =>
        numberToCurrencyString(row.received, {
          fallback: '-',
          currency: row.accountCurrency,
        }),
    },
    {
      key: 'source',
      label: 'Source',
      weight: 1,
      render: (row) => BankAccountOperationSourceLabels[row.source],
    },
  ];

  return (
    <>
      <Table
        disableSorting
        columns={columns}
        rows={data.results}
        isLoading={
          listBankAccountOperationsQuery.isFetching ||
          pagination.isPaginationChanging
        }
        pagination={pagination}
        rowsTotalCount={data.totalCount}
        isExpandableRow={(row) =>
          (actionsByOperationId.get(row.id)?.length ?? 0) > 0
        }
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <Table
            borderless
            columns={actionColumns}
            rows={actionsByOperationId.get(row.id) ?? []}
            disableSorting
          />
        )}
        actions={[
          {
            label: 'Unreconcile',
            icon: LinkOff,
            onClick: (row) => setRowToUnreconcile(row),
          },
        ]}
      />
      <ConfirmDialog
        open={!!rowToUnreconcile}
        onClose={() => setRowToUnreconcile(null)}
        title="Unreconcile this transaction?"
        content="This will reverse the reconciliation and return the transaction to unreconciled status."
        maxWidth="sm"
        loading={unreconcile.isPending}
        actions={{
          confirm: {
            onClick: () => {
              if (!rowToUnreconcile) return;
              unreconcile.mutate({
                vehicleId: bankAccount.vehicleId,
                bankAccountId: bankAccount.id,
                bankAccountOperationId: rowToUnreconcile.id,
              });
            },
            label: 'Unreconcile',
            color: 'error',
          },
        }}
      />
    </>
  );
};

export default ReconciledTransactions;
